import * as React from "react"
import { useQuery } from "@tanstack/react-query"

import { FinanceResource } from "@/features/reports/model/report-registry-types"
import type { NormalizedReport } from "@/features/reports/model/normalized-report"
import {
  adaptActivityBudgetPerformance,
  adaptDepartmentSummary,
  adaptFundActivity,
  adaptFundUtilization,
  adaptReconciliationSummary,
} from "@/features/reports/services/adapters/finance-resource-adapters"
import { financeResourceApi, type FinanceResourceScope } from "@/features/reports/services/finance-resource-api"

/** Per-resource runtime inputs resolved from the report config (fund id, activity id, date range). */
export interface FinanceResourceInputs {
  fundId?: string
  activityId?: string
  dateFrom?: string
  dateTo?: string
}

export interface FinanceResourceReportResult {
  data: NormalizedReport | undefined
  raw: unknown
  isLoading: boolean
  isError: boolean
  /** The rejection itself, so a caller can tell a REFUSAL from a failure (ADR-0024). */
  error: unknown
  isFetching: boolean
  refetch: () => void
}

/**
 * Fetches a finance-resource report (the endpoints built for the catalog beyond the reporting engine) and
 * adapts it into the normalized `ReportResult` the shell renders. One React Query per resource; the switch
 * keeps each resource's endpoint + adapter together. Only implemented resources return data.
 */
export function useFinanceResourceReport(
  resource: FinanceResource | null,
  base: FinanceResourceScope | null,
  inputs: FinanceResourceInputs = {},
): FinanceResourceReportResult {
  // Resources needing runtime inputs gate their query until those inputs are present.
  const ready =
    resource === FinanceResource.FundActivity
      ? Boolean(inputs.fundId && inputs.dateFrom && inputs.dateTo)
      : resource === FinanceResource.ActivityBudgetPerformance
        ? Boolean(inputs.activityId)
        : true

  const query = useQuery({
    queryKey: [
      "finance-reports",
      "resource",
      resource,
      base?.workspaceId,
      base?.branchId,
      base?.scope,
      inputs.fundId,
      inputs.activityId,
      inputs.dateFrom,
      inputs.dateTo,
    ],
    enabled: Boolean(resource) && Boolean(base) && ready,
    queryFn: async () => {
      if (!resource || !base) throw new Error("Missing resource or scope")
      switch (resource) {
        case FinanceResource.Reconciliation:
          return financeResourceApi.reconciliationSummary(base)
        case FinanceResource.FundUtilization:
          return financeResourceApi.fundUtilization(base)
        case FinanceResource.FundActivity:
          return financeResourceApi.fundActivity(base, {
            fundId: inputs.fundId as string,
            dateFrom: inputs.dateFrom as string,
            dateTo: inputs.dateTo as string,
          })
        case FinanceResource.ActivityBudgetPerformance:
          return financeResourceApi.activityBudgetPerformance(base, { activityId: inputs.activityId as string })
        case FinanceResource.DepartmentSummary:
          return financeResourceApi.departmentSummary(base, { dateFrom: inputs.dateFrom, dateTo: inputs.dateTo })
        default:
          throw new Error(`Unsupported finance resource: ${resource}`)
      }
    },
  })

  const data = React.useMemo<NormalizedReport | undefined>(() => {
    if (!query.data) return undefined

    if (resource === FinanceResource.Reconciliation) {
      return adaptReconciliationSummary(query.data as Awaited<ReturnType<typeof financeResourceApi.reconciliationSummary>>)
    }
    if (resource === FinanceResource.FundUtilization) {
      return adaptFundUtilization(query.data as Awaited<ReturnType<typeof financeResourceApi.fundUtilization>>)
    }
    if (resource === FinanceResource.FundActivity) {
      return adaptFundActivity(query.data as Awaited<ReturnType<typeof financeResourceApi.fundActivity>>)
    }
    if (resource === FinanceResource.ActivityBudgetPerformance) {
      return adaptActivityBudgetPerformance(
        query.data as Awaited<ReturnType<typeof financeResourceApi.activityBudgetPerformance>>,
      )
    }
    if (resource === FinanceResource.DepartmentSummary) {
      return adaptDepartmentSummary(query.data as Awaited<ReturnType<typeof financeResourceApi.departmentSummary>>)
    }

    return undefined
  }, [query.data, resource])

  return {
    data,
    raw: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  }
}
